import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
} from "recharts";
import { Card, Col, Row, Statistic, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  MinusOutlined,
  RiseOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  aggregateByPeriod,
  computeCohortInsights,
  formatCompact,
  num,
} from "./cohortInsights";
import { formatNumber } from "../../../utils/formatters";

const { Text } = Typography;

const chartColors = {
  rph: "#0891b2",
  hours: "#722ed1",
  coverage: "#52c41a",
};

function TrendIcon({ delta }) {
  if (delta == null || Number.isNaN(delta)) return <MinusOutlined />;
  if (delta > 0.01) return <ArrowUpOutlined style={{ color: "#52c41a" }} />;
  if (delta < -0.01) return <ArrowDownOutlined style={{ color: "#cf1322" }} />;
  return <MinusOutlined style={{ color: "#8c8c8c" }} />;
}

const CohortCharts = ({ rows = [], granularity = "weekly" }) => {
  const series = useMemo(
    () => aggregateByPeriod(rows, granularity),
    [rows, granularity],
  );

  const insights = useMemo(
    () => computeCohortInsights(rows, granularity),
    [rows, granularity],
  );

  if (!rows.length) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: { padding: "12px 16px" } }}>
            <Statistic
              title="Volume (in range)"
              value={formatCompact(insights.totalHours, 1)}
              suffix="h"
              prefix={<ThunderboltOutlined style={{ color: chartColors.hours }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatCompact(insights.totalUnits, 0)} units · avg RPH{" "}
              {formatNumber(insights.avgWeightedRph, 1, 2)}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: { padding: "12px 16px" } }}>
            <Statistic
              title="Avg contributor coverage"
              value={insights.avgProductivityCoveragePct.toFixed(0)}
              suffix="%"
              prefix={<TeamOutlined style={{ color: chartColors.coverage }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Share of cohort with productivity this period
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: { padding: "12px 16px" } }}>
            <Statistic
              title="Peak RPH (row)"
              value={
                insights.peakRph
                  ? formatNumber(insights.peakRph.rph, 1, 2)
                  : "—"
              }
              prefix={<RiseOutlined style={{ color: chartColors.rph }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {insights.peakRph?.label || "—"}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" styles={{ body: { padding: "12px 16px" } }}>
            <Statistic
              title="Mid-range RPH trend"
              value={insights.trendDeltaRph != null ? insights.trendDeltaRph.toFixed(2) : "—"}
              prefix={<TrendIcon delta={insights.trendDeltaRph} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {insights.trendLabel}
            </Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card
            size="small"
            title={
              granularity === "weekly"
                ? "Productivity intensity by week ending"
                : "Productivity intensity by day"
            }
            styles={{ body: { paddingTop: 8 } }}
          >
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="periodLabel"
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    label={{
                      value: "RPH",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11 },
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    label={{
                      value: "Hours",
                      angle: 90,
                      position: "insideRight",
                      style: { fontSize: 11 },
                    }}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "weightedRph")
                        return [formatNumber(num(value), 1, 2), "RPH"];
                      if (name === "totalHours")
                        return [formatNumber(num(value), 1, 2), "Hours"];
                      return [value, name];
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.period
                        ? String(payload[0].payload.period)
                        : ""
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId="right"
                    dataKey="totalHours"
                    name="Total hours"
                    fill={chartColors.hours}
                    fillOpacity={0.35}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="weightedRph"
                    name="RPH (volume-weighted)"
                    stroke={chartColors.rph}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            size="small"
            title="Cohort engagement (avg % with productivity)"
            styles={{ body: { paddingTop: 8 } }}
          >
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="periodLabel" tick={{ fontSize: 11 }} />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    unit="%"
                  />
                  <Tooltip
                    formatter={(value) => [`${num(value).toFixed(1)}%`, "Coverage"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="avgCoveragePct"
                    name="Coverage"
                    fill={chartColors.coverage}
                    fillOpacity={0.2}
                    stroke={chartColors.coverage}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {insights.coverageRange && (
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                Row-level coverage spans {insights.coverageRange.min.toFixed(0)}%–
                {insights.coverageRange.max.toFixed(0)}% across cohort × period
                combinations.
              </Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CohortCharts;
